import { useState, useEffect, useMemo } from "react"

import ErrorMessage from "components/forms/error-message"
import _ from "lodash"
import Label from "components/forms/label"
import Input from "components/forms/input"
import { lang } from "config"
import MapPicker from "components/map-picker"

const DEFAULT_OPEN_HOURS = {
  regular: [
    { day: "Mon-Fri", open: "09:00", close: "18:00" },
    { day: "Sat", open: "10:00", close: "16:00" },
  ],
  timezone: "Asia/Jakarta",
}

const Form = ({ data = {}, errors, onSubmit }) => {
  const id = data.id ?? undefined
  const [name, setName] = useState(data.name ?? "")
  const [code, setCode] = useState(data.code ?? "")
  const [isHo, setIsHo] = useState(Boolean(data.is_ho))
  // Branch baru default belum live (calon tujuan PO HO, masih di sistem lama).
  const [isLive, setIsLive] = useState(_.isEmpty(data) ? false : Boolean(data.is_live))
  const [contact, setContact] = useState(data.contact ?? "")
  const [address, setAddress] = useState(data.address ?? "")
  const [localErrors, setLocalErrors] = useState({
    latitude: null,
    longitude: null,
    open_hours: null,
  })

  const [latitude, setLatitude] = useState(
    typeof data.latitude === "number"
      ? data.latitude
      : data.latitude !== undefined && data.latitude !== null && data.latitude !== ""
        ? Number(data.latitude)
        : null
  )

  const [longitude, setLongitude] = useState(
    typeof data.longitude === "number"
      ? data.longitude
      : data.longitude !== undefined && data.longitude !== null && data.longitude !== ""
        ? Number(data.longitude)
        : null
  )

  // NEW: image
  const [image, setImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(data.image_url ?? null)

  const isValidNumber = (v) => typeof v === "number" && Number.isFinite(v)
  const hasLocation = useMemo(
    () => isValidNumber(latitude) && isValidNumber(longitude),
    [latitude, longitude]
  )

  // NEW: open_hours + display_note
  const initialOpenHoursText = useMemo(() => {
    // kalau backend sudah simpan JSON object / string, kita handle dua-duanya
    const v = data.open_hours
    if (!v) return JSON.stringify(DEFAULT_OPEN_HOURS, null, 2)
    if (typeof v === "string") {
      try {
        const obj = JSON.parse(v)
        return JSON.stringify(obj, null, 2)
      } catch {
        // kalau string tapi bukan JSON valid, tampilkan apa adanya biar user bisa benerin
        return v
      }
    }
    if (typeof v === "object") return JSON.stringify(v, null, 2)
    return JSON.stringify(DEFAULT_OPEN_HOURS, null, 2)
  }, [data.open_hours])

  const [openHoursText, setOpenHoursText] = useState(initialOpenHoursText)
  const [displayNote, setDisplayNote] = useState(data.display_note ?? "")

  useEffect(() => {
    if (!image) return
    const url = URL.createObjectURL(image)
    setImagePreview(url)
    return () => URL.revokeObjectURL(url)
  }, [image])

  useEffect(() => {
    setName(data.name ?? "")
    setCode(data.code ?? "")
    setIsHo(Boolean(data.is_ho))
    setIsLive(_.isEmpty(data) ? false : Boolean(data.is_live))
    setContact(data.contact ?? "")
    setAddress(data.address ?? "")
    setLatitude(
      data.latitude !== null && data.latitude !== undefined && data.latitude !== ""
        ? Number(data.latitude)
        : null
    )
    setLongitude(
      data.longitude !== null && data.longitude !== undefined && data.longitude !== ""
        ? Number(data.longitude)
        : null
    )
    setImage(null)
    setImagePreview(data.image_url ?? null)

    // reset tambahan
    setOpenHoursText(() => {
      const v = data.open_hours
      if (!v) return JSON.stringify(DEFAULT_OPEN_HOURS, null, 2)
      if (typeof v === "string") {
        try {
          const obj = JSON.parse(v)
          return JSON.stringify(obj, null, 2)
        } catch {
          return v
        }
      }
      if (typeof v === "object") return JSON.stringify(v, null, 2)
      return JSON.stringify(DEFAULT_OPEN_HOURS, null, 2)
    })
    setDisplayNote(data.display_note ?? "")
    setLocalErrors({ latitude: null, longitude: null, open_hours: null })
  }, [data?.id])

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] ?? null
    setImage(file)
  }

  const parseOpenHours = (text) => {
    // return { ok, value, error }
    try {
      const v = JSON.parse(text)
      if (!v || typeof v !== "object") {
        return { ok: false, value: null, error: "open_hours harus berupa JSON object." }
      }
      // validasi ringan minimal: timezone string
      if (!v.timezone || typeof v.timezone !== "string") {
        return { ok: false, value: null, error: "open_hours.timezone wajib string (contoh: Asia/Jakarta)." }
      }
      // regular optional tapi kalau ada harus array
      if (v.regular !== undefined && !Array.isArray(v.regular)) {
        return { ok: false, value: null, error: "open_hours.regular harus array." }
      }
      return { ok: true, value: v, error: null }
    } catch (err) {
      return { ok: false, value: null, error: "Format JSON open_hours tidak valid." }
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const nextLocalErrors = { latitude: null, longitude: null, open_hours: null }

    if (!isValidNumber(latitude)) nextLocalErrors.latitude = "Latitude wajib diisi (pilih lokasi di map)."
    if (!isValidNumber(longitude)) nextLocalErrors.longitude = "Longitude wajib diisi (pilih lokasi di map)."

    const parsed = parseOpenHours(openHoursText)
    if (!parsed.ok) nextLocalErrors.open_hours = parsed.error

    setLocalErrors(nextLocalErrors)
    if (nextLocalErrors.latitude || nextLocalErrors.longitude || nextLocalErrors.open_hours) return

    onSubmit({
      id,
      name,
      code,
      is_ho: isHo,
      is_live: isLive,
      contact,
      address,
      latitude,
      longitude,
      image,

      // NEW
      open_hours: parsed.value, // kirim object (biar backend encode JSON)
      display_note: displayNote,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="name" value={lang.name} />
          <Input onChange={(e) => setName(e.target.value)} error={errors.name} value={name} id="name" />
          <ErrorMessage error={errors.name} />
        </div>

        <div>
          <Label htmlFor="code" value={lang.code} />
          <Input onChange={(e) => setCode(e.target.value)} error={errors.code} value={code} id="code" />
          <ErrorMessage error={errors.code} />
        </div>

        <div className="sm:col-span-2">
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              id="is_ho"
              checked={isHo}
              onChange={(e) => setIsHo(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm">
              <span className="font-medium">Set sebagai Head Office (HO)</span>
              <span className="block text-xs text-neutral-500 mt-0.5">
                Branch yang ditandai HO akan otomatis muncul di daftar supplier, sehingga outlet lain bisa melakukan Purchase Order ke HO. Stok HO akan berkurang saat outlet menerima goods receipt dari PO tersebut. Boleh ada lebih dari satu HO.
              </span>
            </span>
          </label>
          <ErrorMessage error={errors.is_ho} />
        </div>

        <div className="sm:col-span-2">
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              id="is_live"
              checked={isLive}
              onChange={(e) => setIsLive(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm">
              <span className="font-medium">Sudah live di Atalla CMS</span>
              <span className="block text-xs text-neutral-500 mt-0.5">
                Outlet yang belum live (masih pakai sistem lama) tetap bisa dipilih sebagai tujuan PO / faktur / DO dari HO, tapi TIDAK akan muncul di daftar toko aplikasi Yoiboi. Centang setelah outlet resmi pindah ke sistem baru.
              </span>
            </span>
          </label>
          <ErrorMessage error={errors.is_live} />
        </div>

        <div>
          <Label htmlFor="contact" value={lang.contact} />
          <Input onChange={(e) => setContact(e.target.value)} error={errors.contact} value={contact} id="contact" />
          <ErrorMessage error={errors.contact} />
        </div>

        {/* NEW: Image upload */}
        <div className="sm:col-span-2">
          <Label htmlFor="image" value="Image" />
          <input
            id="image"
            name="image"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className={`${
              errors.image ? "border-red-200" : "border-neutral-200"
            } block w-full px-2 py-2 mt-1 text-sm transition border focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200`}
          />
          <ErrorMessage error={errors.image} />

          {imagePreview ? (
            <div className="mt-3">
              <img
                src={imagePreview}
                alt="preview"
                className="h-32 w-32 rounded-xl object-cover border border-neutral-200"
              />
            </div>
          ) : null}
        </div>

        <div className="sm:col-span-2">
          <Label value="Location (Google Maps)" />
          <MapPicker
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
            value={{ lat: latitude, lng: longitude }}
            onChange={({ lat, lng }) => {
              setLatitude(lat)
              setLongitude(lng)
              setLocalErrors((prev) => ({ ...prev, latitude: null, longitude: null }))
            }}
          />

          <div className="grid grid-cols-1 gap-4 mt-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="latitude" value="Latitude" />
              <Input id="latitude" value={latitude ?? ""} readOnly />
              <ErrorMessage error={localErrors.latitude || errors.latitude} />
            </div>
            <div>
              <Label htmlFor="longitude" value="Longitude" />
              <Input id="longitude" value={longitude ?? ""} readOnly />
              <ErrorMessage error={localErrors.longitude || errors.longitude} />
            </div>
          </div>

          <ErrorMessage error={errors.latitude} />
          <ErrorMessage error={errors.longitude} />
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="address" value={lang.address} />
          <textarea
            onChange={(e) => setAddress(e.target.value)}
            value={address}
            name="address"
            id="address"
            rows="5"
            className={`${
              errors.address ? "border-red-200" : "border-neutral-200"
            } block w-full px-2 py-2 mt-1 text-sm transition border focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200`}
          />
          <ErrorMessage error={errors.address} />
        </div>

        {/* NEW: open_hours */}
        <div className="sm:col-span-2">
          <Label htmlFor="open_hours" value="Open Hours (JSON)" />
          <textarea
            id="open_hours"
            name="open_hours"
            rows="10"
            value={openHoursText}
            onChange={(e) => {
              setOpenHoursText(e.target.value)
              setLocalErrors((prev) => ({ ...prev, open_hours: null }))
            }}
            className={`${
              localErrors.open_hours || errors.open_hours ? "border-red-200" : "border-neutral-200"
            } font-mono block w-full px-2 py-2 mt-1 text-sm transition border focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200`}
            placeholder={JSON.stringify(DEFAULT_OPEN_HOURS, null, 2)}
          />
          <ErrorMessage error={localErrors.open_hours || errors.open_hours} />
          <div className="mt-2 text-xs text-neutral-500">
            Format contoh sudah diisi. Pastikan JSON valid.
          </div>
        </div>

        {/* NEW: display_note */}
        <div className="sm:col-span-2">
          <Label htmlFor="display_note" value="Display Note" />
          <textarea
            id="display_note"
            name="display_note"
            rows="4"
            value={displayNote}
            onChange={(e) => setDisplayNote(e.target.value)}
            className={`${
              errors.display_note ? "border-red-200" : "border-neutral-200"
            } block w-full px-2 py-2 mt-1 text-sm transition border focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200`}
            placeholder="Contoh: Tutup sementara untuk renovasi / Parkir tersedia di belakang / dsb."
          />
          <ErrorMessage error={errors.display_note} />
        </div>
      </div>

      <div className="text-xs">
        <button
          type="submit"
          className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90"
        >
          <span>{`${_.isEmpty(data) ? lang.create : lang.update}`}</span>
        </button>
        {!hasLocation ? (
          <div className="mt-2 text-xs text-neutral-500">
            Pilih lokasi di map dulu untuk mengisi latitude & longitude.
          </div>
        ) : null}
      </div>
    </form>
  )
}

export default Form